import { Icon } from '@ps/isp-web-component-library';
import { PriceBoost } from '@ps/isp-web-component-library/icons/PriceBoost';
import { getIsMatchOddsNinetyMarket, getIsTwoUpMarket } from '@ps/web-sports-utils';
import React from 'react';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { type RootState } from '@swf/core/store';
import { EachWayPlaceTerms, ExpandablePanel, Title } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type {
  CompoundMarketTemplateProps,
  EventLevelTemplateId,
  MarketLevelTemplateId,
  MarketTemplateProps,
  SingleMarketTemplateProps
} from '@swf/sports/types/templates';
import { isCompoundMarketTemplateProps } from '@swf/sports/utils/isCompoundMarketTemplateProps';
import { marketTemplateTranslationMap } from '@swf/sports/utils/marketTemplateTranslationMap';

import { TitleWithHighlight } from '../TitleWithHighlight/TitleWithHighlight';

import { MarketTemplate } from './MarketTemplate';
import { TXTWCL_odds_boost } from './MarketTemplatePanel.messages.json';
import styles from './MarketTemplatePanel.scss';
import MarketTitleWithIcons from './components/MarketTitleWithIcons/MarketTitleWithIcons';
import TermsAndConditions from './components/TermsAndConditions/TermsAndConditions';

export interface Props {
  isOpenInitially: boolean;
  highlight?: string;
  templateId: MarketLevelTemplateId | EventLevelTemplateId;
}

const SingleMarketTemplatePanel = ({
  highlight,
  isOpenInitially,
  ...restProps
}: Props & SingleMarketTemplateProps) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const { marketName, placeTerms, isStarSubEnabled, isSgmEligible, marketType } =
    useSelector((state: RootState) => widgetSelect.market(state, restProps.marketId)) ?? {};
  const { formatMessage } = useRegulatoryIntl();
  const isTwoUpMarket = getIsTwoUpMarket(marketType);
  const isMatchOddsNinetyMarket = getIsMatchOddsNinetyMarket(marketType);
  const isOddsBoost = restProps.templateId === 'ODDS_BOOST';
  const { termsAndConditions } = restProps.templateConfig || {};

  let title: React.ReactNode = marketName;
  const titleString = marketName;

  if (highlight?.length && titleString) {
    title = <TitleWithHighlight title={titleString} partToHighlight={highlight} />;
  }

  if (isOddsBoost) {
    title = (
      <span className={styles.oddsboostTitle}>
        <Icon icon={PriceBoost} label={formatMessage(TXTWCL_odds_boost)} size="xs" />
        <span className={styles.oddsboostTitleText}>{title}</span>
      </span>
    );
  }

  if (isStarSubEnabled || isSgmEligible || isTwoUpMarket || isMatchOddsNinetyMarket) {
    title = (
      <MarketTitleWithIcons
        title={title}
        isSgmEligible={isSgmEligible}
        isStarSubEnabled={isStarSubEnabled}
        marketType={marketType}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <ExpandablePanel
        isOpenInitially={isOpenInitially}
        title={<Title>{title}</Title>}
        isOddsBoost={isOddsBoost}
        panelName={marketName}
      >
        <>
          <EachWayPlaceTerms placeTerms={placeTerms} className={styles.eachWay} variant="long" />
          {termsAndConditions && <TermsAndConditions termsAndConditions={termsAndConditions} />}

          <MarketTemplate {...restProps} />
        </>
      </ExpandablePanel>
    </div>
  );
};

const CompoundMarketTemplatePanel = ({
  highlight,
  isOpenInitially,
  ...restProps
}: Props & CompoundMarketTemplateProps) => {
  const { config, marketIds } = restProps;
  const { termsAndConditions } = config;

  const { formatMessage } = useRegulatoryIntl();
  const { widgetSelect } = useEventMarketTemplatesWidget();

  const markets = useSelector((state: RootState) => widgetSelect.markets(state, marketIds));
  const isStarSubEnabled = Object.values(markets).some((market) => market?.isStarSubEnabled);
  const isSgmEligible = Object.values(markets).some((market) => market?.isSgmEligible);
  const titleTranslation = marketTemplateTranslationMap[config.title];

  let title: React.ReactNode = titleTranslation ? formatMessage(titleTranslation) : '';
  const panelName = title as string;

  if (highlight?.length) {
    title = <TitleWithHighlight title={panelName} partToHighlight={highlight} />;
  }

  if (isSgmEligible || isStarSubEnabled) {
    title = (
      <MarketTitleWithIcons
        title={title}
        isStarSubEnabled={isStarSubEnabled}
        isSgmEligible={isSgmEligible}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <ExpandablePanel
        isOpenInitially={isOpenInitially}
        title={<Title>{title}</Title>}
        panelName={panelName}
      >
        {termsAndConditions && <TermsAndConditions termsAndConditions={termsAndConditions} />}
        <MarketTemplate {...restProps} />
      </ExpandablePanel>
    </div>
  );
};

export const MarketTemplatePanel = (props: Props & MarketTemplateProps) =>
  isCompoundMarketTemplateProps(props) ? (
    <CompoundMarketTemplatePanel {...props} />
  ) : (
    <SingleMarketTemplatePanel {...props} />
  );
